import React, { useCallback, useContext, useMemo } from "react";

import cx from "classnames";
import moment from "moment";
import { CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { AxisDomainItem } from "recharts/types/util/types.d";
import type { GraphQLTaggedNode, OperationType } from "relay-runtime";

import ErrorBoundary from "~/common/ErrorBoundary";
import { hasOwnProperty } from "~/lib/js";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { LoadingSpinner } from "~/ui";
import ChartWrapper, { type ChartWrapperProps } from "~/ui/Charts/ChartWrapper";
import { DEFAULT_COLOURS, EMPTY_STATE_MESSAGE, ERROR_MESSAGE, LOADING_MESSAGE, WEEK_TAB } from "~/ui/Charts/constants";
import { generateCartesianMockData } from "~/ui/Charts/utils";
import CustomTooltip from "~/workspaceAnalytics/Tooltip";
import { AnalyticsContext } from "~/workspaceAnalytics/types";

import styles from "./CartesianChart.module.css";

export type CartesianChartProps<TQuery extends OperationType> = {
	chart: React.FunctionComponent<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
	query: GraphQLTaggedNode;
	formatData(data: TQuery["response"]): Array<object> | readonly object[];
	xAxisKey?: string;
	xAxisFormatter?: (value: string) => string;
	yAxisKey?: string;
	yAxisFormatter?: (value: string) => string;
	yAxisRange?: [AxisDomainItem, AxisDomainItem];
	colours?: { [key: string]: string };
	generateItem(key: string, colour: string): React.ReactElement;
	tooltipValueSuffix?: string;
	hideLegend?: boolean;
	paywallApplicable?: boolean;
	chartClassName?: string;
} & ChartWrapperProps;

interface MockChartProps {
	topLabel?: boolean;
	chart: React.FunctionComponent<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
	generateItem(key: string, colour: string): React.ReactElement;
}

const mockData = generateCartesianMockData(30, 100);

function MockChart({ topLabel, chart: Chart, generateItem, children }: React.PropsWithChildren<MockChartProps>) {
	const items: Array<React.ReactElement> = useMemo(() => {
		return [generateItem("value", DEFAULT_COLOURS[0])];
	}, [generateItem]);

	return (
		<div className={styles.mockChartContainer}>
			<ResponsiveContainer height={220}>
				<Chart data={mockData} className={cx(styles.cartesianChart, styles.mockChart)}>
					<CartesianGrid />
					<XAxis dataKey="date" tickLine={false} tickMargin={12} />
					<YAxis dataKey="value" tickLine={false} tickMargin={12} />
					{items.map(item => item)}
				</Chart>
			</ResponsiveContainer>
			{children && <div className={cx(styles.label, { [styles.topLabel]: topLabel })}>{children}</div>}
		</div>
	);
}

function CartesianChart<TQuery extends OperationType>({
	chart: Chart,
	query,
	formatData,
	xAxisKey = "date",
	xAxisFormatter,
	yAxisKey,
	yAxisFormatter,
	yAxisRange,
	colours,
	generateItem,
	tooltipValueSuffix,
	hideLegend,
	chartClassName,
	activeTab,
}: CartesianChartProps<TQuery>) {
	const { otherUserId, rangeFilter: lastDays, useHierarchy } = useContext(AnalyticsContext);

	const queryData = useLazyLoadQuery<TQuery>(
		query,
		{ lastDays, otherUserId, useHierarchy },
		{ fetchPolicy: "store-or-network" },
	);

	const data = useMemo(() => formatData(queryData), [queryData, formatData]);

	const items: Array<React.ReactElement> = useMemo(() => {
		if (!data || data.length === 0) {
			return [];
		}
		const result = [];
		const itemDataKeys = Object.keys(data[0]).filter(k => k !== xAxisKey);
		let colourIndex = 0;
		for (let i = 0; i < itemDataKeys.length; i++) {
			let colour;
			if (colours && hasOwnProperty(colours, itemDataKeys[i])) {
				colour = colours[itemDataKeys[i]];
			} else {
				colour = DEFAULT_COLOURS[colourIndex];
				colourIndex++;
				if (colourIndex >= DEFAULT_COLOURS.length) {
					colourIndex = 0;
				}
			}
			result.push(generateItem(itemDataKeys[i], colour));
		}
		return result;
	}, [data, colours, generateItem, xAxisKey]);

	const defaultXAxisFormatter = useCallback(
		(tick: string) => {
			const formattedDate = moment(tick);
			if (!formattedDate.isValid()) {
				return tick;
			}
			return formattedDate.format(activeTab === WEEK_TAB ? "ddd" : "MMM D");
		},
		[activeTab],
	);

	const dataSum = useMemo(() => {
		if (!data) {
			return 0;
		}
		let sum = 0;
		const itemDataKeys = Object.keys(data[0]).filter(k => k !== xAxisKey);
		for (let i = 0; i < data.length; i++) {
			for (let j = 0; j < itemDataKeys.length; j++) {
				// @ts-ignore
				sum += data[i][itemDataKeys[j]];
			}
		}
		return sum;
	}, [data, xAxisKey]);

	if (dataSum === 0) {
		return (
			<MockChart chart={Chart} generateItem={generateItem}>
				<h3>{EMPTY_STATE_MESSAGE}</h3>
			</MockChart>
		);
	}

	return (
		<ResponsiveContainer className={styles.chart} height={220}>
			<Chart data={data} className={cx(styles.cartesianChart, chartClassName)}>
				<CartesianGrid />
				<XAxis
					dataKey={xAxisKey}
					tickLine={false}
					tickMargin={12}
					tickFormatter={xAxisFormatter || defaultXAxisFormatter}
				/>
				<YAxis dataKey={yAxisKey} tickLine={false} tickMargin={12} tickFormatter={yAxisFormatter} domain={yAxisRange} />
				<Tooltip
					content={<CustomTooltip valueSuffix={tooltipValueSuffix} />}
					cursor={{ fill: "var(--baseLayerColor)" }}
				/>
				{!hideLegend && (
					<Legend
						align="left"
						iconType="circle"
						iconSize={8}
						// The margin property is not working for the Legend component
						wrapperStyle={{ bottom: "-12px" }}
					/>
				)}
				{items.map(item => item)}
			</Chart>
		</ResponsiveContainer>
	);
}

export default function CartesianChartWrapper<TQuery extends OperationType>({
	chart: Chart,
	generateItem,
	activeTab,
	paywallApplicable,
	...props
}: CartesianChartProps<TQuery>) {
	const { hasAccessToAnalytics } = useContext(AnalyticsContext);
	return (
		<ChartWrapper activeTab={activeTab} paywallApplicable={paywallApplicable} {...props}>
			{!paywallApplicable || hasAccessToAnalytics ? (
				<ErrorBoundary
					fallback={
						<MockChart chart={Chart} generateItem={generateItem}>
							<h3>{ERROR_MESSAGE}</h3>
						</MockChart>
					}
				>
					<React.Suspense
						fallback={
							<MockChart chart={Chart} generateItem={generateItem}>
								<h3>{LOADING_MESSAGE}</h3>
								<LoadingSpinner dimmer={false} inline />
							</MockChart>
						}
					>
						<CartesianChart<TQuery> chart={Chart} generateItem={generateItem} activeTab={activeTab} {...props} />
					</React.Suspense>
				</ErrorBoundary>
			) : (
				<MockChart chart={Chart} generateItem={generateItem} topLabel></MockChart>
			)}
		</ChartWrapper>
	);
}
